import { useState, type FormEvent, type ReactNode } from "react";
import {
  Alert,
  ApiError,
  Button,
  cn,
  formatUsPhone,
  type PartnerMeResponse,
} from "@dearbill/shared";
import { useApi } from "../auth/useApi";
import { usePartnerMe } from "../auth/usePartnerMe";

/**
 * Account profile page (`/profile`), the signed-in partner user's own account.
 *
 * Top-level and org-independent, reachable from the top bar's name link on any
 * authenticated page. Role is omitted because it is per org (a partner can be an
 * org admin in one org and a member in another); the org list shows each org's
 * role.
 *
 * A detail list with Name, Email and Phone rows. Name reads as text, with a
 * pencil button that flips that row into edit mode (inline input and Save);
 * Email and Phone are read-only. Saving sends `PATCH /api/partner/me` with
 * `{ name }`, which must be non-blank or the server answers 422. On success the
 * server's `PartnerMeResponse` is lifted into the shared `PartnerMeContext`
 * (`setMe`), so the new name shows everywhere that reads `me`, including the top
 * bar, without a reload.
 *
 * When `me` is `null` (a `/me` failure), edit mode starts with an empty input.
 */
export function UserProfileRoute(): ReactNode {
  const api = useApi();
  const { me, setMe } = usePartnerMe();

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(me?.name ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const trimmed = name.trim();
  const unchanged = trimmed === (me?.name ?? "");
  const canSave = !busy && (unchanged || trimmed.length > 0);

  const startEdit = (): void => {
    setName(me?.name ?? "");
    setError(null);
    setSaved(false);
    setEditing(true);
  };

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (!canSave) return;
    // Unchanged Save is the intended exit from edit mode (no Cancel button):
    // a no-op that returns to read-only without a network call or banner.
    if (unchanged) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setError(null);
    setSaved(false);
    api<PartnerMeResponse>("/api/partner/me", {
      method: "PATCH",
      body: { name: trimmed },
    })
      .then((updated) => {
        setBusy(false);
        setSaved(true);
        setEditing(false);
        setName(updated.name ?? "");
        setMe(updated);
      })
      .catch((err: unknown) => {
        setBusy(false);
        // A 401 has cleared the session in `useApi`, and the redirect takes over.
        if (err instanceof ApiError && err.status === 401) return;
        setError("We couldn't save your name. Please try again.");
      });
  };

  return (
    <div className="max-w-3xl">
      <h2 className="text-h4 text-midnight">Your profile</h2>

      <dl className="divide-haze border-border-strong mt-4 divide-y rounded-2xl border bg-white">
        <div className="grid grid-cols-1 gap-1 px-5 py-4 sm:grid-cols-3 sm:items-center sm:gap-4">
          <dt className="text-slate-teal text-sm font-medium">Name</dt>
          <dd className="sm:col-span-2">
            {editing ? (
              <form className="flex items-center gap-3" onSubmit={onSubmit} noValidate>
                {/* No visible label, since the row's <dt>Name</dt> labels it;
                    `aria-label` gives the accessible name. The classes are
                    modeled on the shared Input primitive. */}
                <input
                  aria-label="Name"
                  required
                  autoFocus
                  value={name}
                  disabled={busy}
                  onChange={(e) => {
                    setName(e.target.value);
                    setSaved(false);
                  }}
                  className={cn(
                    "border-border bg-input-bg text-midnight block w-full flex-1 rounded-lg border-2 px-3 py-2 text-sm",
                    "placeholder:text-placeholder",
                    "focus:border-primary focus:bg-white focus:outline-none",
                    "disabled:bg-ice disabled:text-cloud-blue disabled:cursor-not-allowed",
                  )}
                />
                {/* The default `md` size, not `sm`, so the edit flow's primary
                    action meets the minimum touch-target size. */}
                <Button type="submit" disabled={!canSave}>
                  {busy ? "Saving…" : "Save"}
                </Button>
              </form>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <span className="text-midnight text-sm">
                  {me?.name ?? <span className="text-placeholder">—</span>}
                </span>
                <button
                  type="button"
                  aria-label="Edit name"
                  onClick={startEdit}
                  className={cn(
                    "text-slate-teal inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                    "hover:bg-ice hover:text-midnight",
                    "focus:ring-haze focus:outline-none focus:ring-2",
                  )}
                >
                  <PencilIcon />
                </button>
              </div>
            )}
          </dd>
        </div>

        <ProfileRow
          label="Email"
          value={me?.email ?? <span className="text-placeholder">—</span>}
        />

        <ProfileRow
          label="Phone"
          value={me?.phone ? formatUsPhone(me.phone) : <span className="text-placeholder">—</span>}
        />
      </dl>

      {error && <Alert tone="error" title={error} className="mt-4" />}
      {saved && (
        <p
          role="status"
          className="border-success-border bg-success-bg text-forest mt-4 rounded-2xl border px-4 py-3 text-sm"
        >
          Your name has been updated.
        </p>
      )}
    </div>
  );
}

/** One read-only label/value row in the profile detail list. */
function ProfileRow({ label, value }: { label: string; value: ReactNode }): ReactNode {
  return (
    <div className="grid grid-cols-1 gap-1 px-5 py-4 sm:grid-cols-3 sm:gap-4">
      <dt className="text-slate-teal text-sm font-medium">{label}</dt>
      <dd className="text-midnight text-sm sm:col-span-2">{value}</dd>
    </div>
  );
}

/**
 * Inline pencil glyph, local because the shared icon set has none. The `d` paths
 * copy the admin Icon's `edit` case.
 */
function PencilIcon(): ReactNode {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}
